import { Button } from "@grafit/design-system/components/molecules/button";
import { toast } from "@grafit/design-system/components/molecules/toast";
import type { TFunction } from "@grafit/i18n";
import { alertDevs, log } from "@grafit/shared/observability/sentry-front";
import { isLocal, isPreview } from "@grafit/shared/utils/envs.front";
import { getAppVersion, getVersionSkew, isBackendReleaseNewer, type VersionSkew } from "@grafit/shared/utils/version";
import { z } from "zod";
import { setBeforeVersionReload, setVersionMismatch } from "../../scaffolding/navigation-strategy";
import { requestServiceWorkerUpdate } from "../../service-worker/register-service-worker";

const TOAST_ID = "app-version-toast";

interface StoredAppVersion {
  version: string;
  date: number;
}

/** Snapshot preserved before a reload attempt overwrites the stored version. */
let storedAppVersionAtLoad: StoredAppVersion | null = null;
let backendLag: { versionPair: string; firstSeenAt: number; reported: boolean } | null = null;
let hasReportedUnknownSkew = false;
let pageLoadedAt = Date.now();

export function handleAppVersionCheck(headers: Headers, t: TFunction) {
  const storedAppVersion = getStoredAppVersionAtLoad();
  const backendVersion = headers.get("x-app-version");
  const frontendVersion = getAppVersion();
  const skew: VersionSkew = isLocal() ? "compatible" : getVersionSkew(backendVersion, frontendVersion);

  if (skew === "unknown") {
    backendLag = null;
    reportUnknownSkew(frontendVersion, backendVersion);
    return;
  }

  const isFrontOutdated = skew === "front-behind";
  const isReleaseDriftDue = skew === "compatible" && isReleaseDriftReloadDue(backendVersion, frontendVersion);
  saveAppOutdatedState(isFrontOutdated);
  setVersionMismatch(isFrontOutdated || isReleaseDriftDue);

  // Reloading serves the same build, so nothing to tell the user.
  if (skew === "front-ahead") {
    reportBackendLagIfStuck(frontendVersion, backendVersion);
    return;
  }

  // The rollout moved on, so a later lag starts a fresh tolerance window.
  backendLag = null;

  if (!isFrontOutdated) {
    return;
  }

  if (isVersionFailedUpdate(frontendVersion)) {
    return;
  }

  if (isVersionStillOutdatedAfterReload(frontendVersion, storedAppVersion)) {
    markVersionAsFailedUpdate(frontendVersion);
    alertDevsAboutFailedUpdate({ frontendVersion, backendVersion, storedAppVersion, skew });
    return;
  }

  requestServiceWorkerUpdate();
  showAppUpdateToast(t);
}

const UPDATE_DETECTION_WINDOW_MS = 30_000;

/** Patches ship several times a day: reloading for each would annoy users, so a tab catches up once a day, on navigation to keep unsaved input. */
function isReleaseDriftReloadDue(backendVersion: string | null, frontendVersion: string): boolean {
  return (
    !isLocal() &&
    !isPreview() &&
    isBackendReleaseNewer(backendVersion, frontendVersion) &&
    new Date(pageLoadedAt).toDateString() !== new Date(Date.now()).toDateString()
  );
}

function isVersionStillOutdatedAfterReload(currentVersion: string, storedVersion: StoredAppVersion): boolean {
  const isSameVersion = storedVersion.version === currentVersion;
  const isWithinTimeWindow = storedVersion.date + UPDATE_DETECTION_WINDOW_MS > Date.now();
  return isSameVersion && isWithinTimeWindow;
}

function markVersionAsFailedUpdate(version: string): void {
  try {
    window.localStorage.setItem(FRONTEND_VERSION_FAILED_UPDATE_KEY, version);
  } catch {
    //pass
  }
}

function isVersionFailedUpdate(version: string): boolean {
  try {
    return window.localStorage.getItem(FRONTEND_VERSION_FAILED_UPDATE_KEY) === version;
  } catch {
    return false;
  }
}

const BACKEND_LAG_TOLERANCE_MS = 5 * 60 * 1000;

/** A rolling deployment swaps ECS tasks in minutes, so only a lag outliving it is worth reporting. */
function reportBackendLagIfStuck(frontendVersion: string, backendVersion: string | null): void {
  const versionPair = `${frontendVersion}<-${backendVersion}`;
  const lag =
    backendLag?.versionPair === versionPair ? backendLag : { versionPair, firstSeenAt: Date.now(), reported: false };
  backendLag = lag;

  if (lag.reported || Date.now() - lag.firstSeenAt < BACKEND_LAG_TOLERANCE_MS) {
    return;
  }

  lag.reported = true;
  log("Backend version stayed behind the frontend version", "warn", { frontendVersion, backendVersion });
}

function reportUnknownSkew(frontendVersion: string, backendVersion: string | null): void {
  if (isPreview() || hasReportedUnknownSkew) {
    return;
  }

  hasReportedUnknownSkew = true;
  log("Could not compare frontend and backend versions", "warn", { frontendVersion, backendVersion });
}

function getNavigationType(): string {
  const [navigationEntry] = window.performance.getEntriesByType("navigation");
  return (navigationEntry as PerformanceNavigationTiming | undefined)?.type ?? "unknown";
}

interface FailedUpdateReport {
  frontendVersion: string;
  backendVersion: string | null;
  storedAppVersion: StoredAppVersion;
  skew: VersionSkew;
}

function alertDevsAboutFailedUpdate({
  frontendVersion,
  backendVersion,
  storedAppVersion,
  skew,
}: FailedUpdateReport): void {
  // prevent backward compatibility tests error trigger
  if (isPreview()) {
    return;
  }

  alertDevs({
    error: new Error("Reloading the app did not update the version as expected, version missmatch"),
    context: {
      frontendVersion,
      storedAppVersion: storedAppVersion.version,
      backendVersion,
      storedAppVersionAgeMs: Date.now() - storedAppVersion.date,
      navigationType: getNavigationType(),
      skew,
    },
  });
}

function showAppUpdateToast(t: TFunction) {
  toast.info(
    t((r) => r["app-update.toast.title"]),
    {
      id: TOAST_ID,
      action: (
        <Button
          variant={{ kind: "primary", renderMode: "outline", size: "m" }}
          onClick={() => {
            markVersionReloadAttempt();
            window.location.reload();
          }}
          className="hover:bg-blue-200/50 hover:text-blue-600"
        >
          {t((r) => r["app-update.toast.action"])}
        </Button>
      ),
      dismissible: false,
      duration: Number.POSITIVE_INFINITY,
    },
  );
}

const APP_IS_OUTDATED_KEY = "is-app-outdated";

function saveAppOutdatedState(isFrontOutdated: boolean) {
  try {
    window.localStorage.setItem(APP_IS_OUTDATED_KEY, isFrontOutdated.toString());
  } catch {
    // pass
  }
}

export function getAppOutdatedState(): boolean {
  try {
    return window.localStorage.getItem(APP_IS_OUTDATED_KEY) === "true";
  } catch {
    return false;
  }
}

export function markVersionReloadAttempt(): void {
  getStoredAppVersionAtLoad();
  saveAppVersion(getAppVersion());
}

const PRELOAD_ERROR_RELOAD_KEY = "preload-error-reload";
/** A chunk still missing after a reload would loop forever: at most one reload per tab, version and window. */
const PRELOAD_ERROR_RELOAD_WINDOW_MS = 5 * 60_000;
let preloadErrorReloadPending = false;

interface BlockedPreloadReload {
  reason: "recent-attempt" | "storage-unavailable";
  previousAttemptAgeMs: number | null;
}
const lastPreloadErrorReloadSchema = z.object({ version: z.string(), at: z.number() });

const readLastPreloadErrorReload = (): z.infer<typeof lastPreloadErrorReloadSchema> | null => {
  try {
    return lastPreloadErrorReloadSchema.parse(
      JSON.parse(window.sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY) ?? ""),
    );
  } catch {
    return null;
  }
};

/** The retry window lets a recovered tab handle a later deploy. */
function claimPreloadErrorReload(): BlockedPreloadReload | null {
  const last = readLastPreloadErrorReload();
  const previousAttemptAgeMs = last ? Date.now() - last.at : null;
  if (
    last?.version === getAppVersion() &&
    previousAttemptAgeMs !== null &&
    previousAttemptAgeMs < PRELOAD_ERROR_RELOAD_WINDOW_MS
  ) {
    return { reason: "recent-attempt", previousAttemptAgeMs };
  }
  try {
    window.sessionStorage.setItem(
      PRELOAD_ERROR_RELOAD_KEY,
      JSON.stringify({ version: getAppVersion(), at: Date.now() }),
    );
    return null;
  } catch {
    return { reason: "storage-unavailable", previousAttemptAgeMs };
  }
}

/** Leave the event uncancelled so Vite can surface the import failure if navigation fails. */
export function handleVitePreloadError(event: Event): void {
  if (preloadErrorReloadPending) {
    return;
  }
  log(`vite:preloadError: ${event.type}`, "warn");
  const blocked = claimPreloadErrorReload();
  if (blocked) {
    alertDevs({
      error: new Error("vite:preloadError reload blocked"),
      context: {
        appVersion: getAppVersion(),
        preloadError: "payload" in event ? event.payload : undefined,
        ...blocked,
      },
    });
    return;
  }
  preloadErrorReloadPending = true;
  markVersionReloadAttempt();
  window.location.reload();
}

/** @internal knip test export */
export const FRONTEND_VERSION_KEY = "frontend-version";
/** @internal knip test export */
export const FRONTEND_VERSION_FAILED_UPDATE_KEY = "frontend-version-failed-update";

function saveAppVersion(version: string) {
  try {
    window.localStorage.setItem(FRONTEND_VERSION_KEY, JSON.stringify({ version, date: Date.now() }));
  } catch {
    // pass
  }
}

const storedSchema = z.object({
  version: z.string(),
  date: z.number(),
});

function getStoredAppVersion(): StoredAppVersion {
  let storedValue: string | null;

  try {
    storedValue = window.localStorage.getItem(FRONTEND_VERSION_KEY);
  } catch {
    return { version: "0.0.0", date: 0 };
  }

  try {
    return storedSchema.parse(JSON.parse(storedValue ?? '{"version":"0.0.0","date":0}'));
  } catch {
    alertDevs({
      error: new Error("couldn't parse FRONTEND_VERSION_KEY"),
      context: {
        FRONTEND_VERSION_KEY: storedValue,
      },
    });
    return { version: "0.0.0", date: Date.now() };
  }
}

function getStoredAppVersionAtLoad(): StoredAppVersion {
  storedAppVersionAtLoad ??= getStoredAppVersion();
  return storedAppVersionAtLoad;
}

/** @internal knip test export */
export function initAppVersionCheck(): void {
  storedAppVersionAtLoad = getStoredAppVersion();
  backendLag = null;
  hasReportedUnknownSkew = false;
  preloadErrorReloadPending = false;
  pageLoadedAt = Date.now();
}

setBeforeVersionReload(markVersionReloadAttempt);
